/**
 * Consecutive Heat Index exposure — persistence of environmental exposure.
 *
 * This module measures how many CONSECUTIVE DAYS an exposure condition held. It is an
 * environmental exposure metric: it says nothing about physiological heat strain,
 * worker health, safety or productivity.
 *
 * The condition has two parts:
 *
 *   Intensity   HI >= T      T is an NWS category boundary (see HI_EXPOSURE_LEVELS)
 *   Duration    E_d(T) >= N  E_d(T) = hours of day d with HI at or above T
 *
 * A day satisfying both is an exposure day; adjacent exposure days form a sequence.
 *
 * Thresholds are NOT redefined here. The hourly Heat Index and its classification come
 * from POST /model/hi (model/hi/hi.py#HI_CATEGORIES); each daily record already carries
 * the hours spent in each category, and "at or above T" is their running sum from T up.
 */

// The NWS category boundaries, mildest first — the same ones model/hi/hi.py classifies
// each hour with. `key` is the field name of that category's hours in a daily record.
export const HI_EXPOSURE_LEVELS = [
    { id: 'caution', key: 'hi_caution', label: 'Caution', thresholdF: 80 },
    { id: 'extremeCaution', key: 'hi_extremeCaution', label: 'Extreme Caution', thresholdF: 91 },
    { id: 'danger', key: 'hi_danger', label: 'Danger', thresholdF: 104 },
    { id: 'extremeDanger', key: 'hi_extremeDanger', label: 'Extreme Danger', thresholdF: 126 },
];

export const HOURS_PER_DAY = 24;

export const levelOf = (levelId) => HI_EXPOSURE_LEVELS.find((level) => level.id === levelId) || null;

// "HI >= 91°F for >= 6 h/day" — every reported number must travel with this.
export function conditionLabel(levelId, minHours) {
    const level = levelOf(levelId);
    if (!level) return '';
    return `HI ≥ ${level.thresholdF}°F for ≥ ${minHours} h/day`;
}

const dayNumber = (date) => Math.round(Date.parse(`${String(date).slice(0, 10)}T00:00:00Z`) / 86400000);

/**
 * Daily exposure duration E_d(T): hours at or above the level, one entry per record.
 *
 * Counts the chosen category and every category above it, so raising the level can only
 * lower the hours. Also carries the record's data-quality counters (model/hi reports
 * valid_hours / missing_hours / forecast_hours per day) so completeness travels with the
 * number instead of being assumed.
 */
export function dailyExposureHours(rows, levelId) {
    const index = HI_EXPOSURE_LEVELS.findIndex((level) => level.id === levelId);
    if (index === -1) return [];

    const keys = HI_EXPOSURE_LEVELS.slice(index).map((level) => level.key);

    return (rows || [])
        .filter((row) => row && row.date)
        .map((row) => {
            const hours = keys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
            // Absent counters mean an older payload: nothing is known to be missing.
            const missingHours = Number(row.missing_hours) || 0;
            const forecastHours = Number(row.forecast_hours) || 0;
            const validHours = row.valid_hours != null ? Number(row.valid_hours) : HOURS_PER_DAY - missingHours;

            return {
                date: String(row.date).slice(0, 10),
                hours,
                validHours,
                missingHours,
                forecastHours,
                complete: missingHours === 0,
                forecast: forecastHours > 0,
            };
        });
}

/**
 * Classify each day against the minimum daily exposure N.
 *
 * Three outcomes, never two: a day short of N whose MISSING hours could still carry it
 * to N is `unknown`, not `false` — the hours simply are not known yet. A day already at
 * or above N qualifies whatever is missing, since more hours can only add to it.
 */
export function classifyExposureDays(daily, minHours) {
    const required = Number(minHours);

    return (daily || []).map((day) => {
        let status;
        if (day.hours >= required) status = 'qualifies';
        else if (day.hours + day.missingHours >= required) status = 'unknown';
        else status = 'no';

        return { ...day, status, qualifies: status === 'qualifies' };
    });
}

const summarizeRun = (run) => {
    const hours = run.map((day) => day.hours);
    const total = hours.reduce((sum, value) => sum + value, 0);

    return {
        start_date: run[0].date,
        end_date: run[run.length - 1].date,
        duration_days: run.length,
        total_exposure_hours: total,
        mean_exposure_hours_per_day: Math.round((total / run.length) * 10) / 10,
        maximum_daily_exposure_hours: Math.max(...hours),
        includes_forecast: run.some((day) => day.forecast),
    };
};

/**
 * Consecutive sequences of qualifying days.
 *
 * Consecutiveness is by CALENDAR DATE: a gap in the dates ends the run, so a missing day
 * never silently joins two sequences. An `unknown` day also ends a confirmed run.
 *
 * `current` is the run ending on the most recent day whose outcome is SETTLED. A day
 * still short of N whose missing hours could yet reach it is `unknown`: it neither
 * confirms nor terminates the run, and is reported as `pending` instead. A day already
 * past N, or one whose remaining hours can no longer reach it, is settled even while
 * incomplete — there the record already answers the question.
 */
export function detectConsecutiveSequences(days, minDays = 1) {
    const minimum = Math.max(1, Number(minDays) || 1);
    const classified = days || [];

    const runs = [];
    let run = [];
    const closeRun = () => {
        if (run.length) runs.push(run);
        run = [];
    };

    classified.forEach((day) => {
        if (day.status !== 'qualifies') {
            closeRun();
            return;
        }
        const previous = run[run.length - 1];
        if (previous && dayNumber(day.date) - dayNumber(previous.date) !== 1) closeRun();
        run.push(day);
    });
    closeRun();

    const events = runs.filter((candidate) => candidate.length >= minimum).map(summarizeRun);
    const longest = runs.reduce((max, candidate) => Math.max(max, candidate.length), 0);

    // The current sequence is measured on the settled part of the record: everything up
    // to and including the last day with a determined outcome.
    const lastSettledIndex = classified.map((day) => day.status !== 'unknown').lastIndexOf(true);
    const settled = lastSettledIndex === -1 ? [] : classified.slice(0, lastSettledIndex + 1);
    const settledRun = runs.find(
        (candidate) => settled.length > 0 && candidate[candidate.length - 1].date === settled[settled.length - 1].date
    );
    const current = settledRun ? settledRun.length : 0;

    const pending = classified.slice(lastSettledIndex + 1);

    return {
        current,
        current_end_date: current ? settled[settled.length - 1].date : null,
        longest,
        events,
        event_count: events.length,
        pending_days: pending.length,
        minimum_consecutive_days: minimum,
    };
}

/**
 * The whole pipeline for one daily record series:
 * hourly HI -> daily exposure hours -> qualifying days -> consecutive sequences.
 */
export function summarizeExposure(rows, { level, minHours, minDays } = {}) {
    const daily = classifyExposureDays(dailyExposureHours(rows, level), minHours);
    const sequences = detectConsecutiveSequences(daily, minDays);

    return {
        condition: conditionLabel(level, minHours),
        threshold_F: levelOf(level) ? levelOf(level).thresholdF : null,
        minimum_hours_per_day: Number(minHours),
        days: daily,
        ...sequences,
    };
}
